import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import ListActions from 'Components/ListActions'
import ListSearch from 'Components/ListSearch'
import PageTitle from 'Components/PageTitle'
import { WithLoader } from 'Components/WithLoader'
import { CatalogList } from 'Services/ProductManagement/Features/Catalog/Components/CatalogList'
import { useDeleteSingleCatalogMutation } from 'Services/ProductManagement/Features/Catalog/catalogApi'
import useCatalogsWithParams from 'Services/ProductManagement/Features/Catalog/Hooks'
import TariffCatalogDrawer from 'Services/ProductManagement/Features/Tariff/Components/TariffCatalogDrawer'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { DeleteModes } from 'Services/ProductManagement/types'
import type { ProductTariffError } from 'Util/ApiClient'
import { PRODUCT_MANAGEMENT_ROOT_URL, ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import useListFunctions from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { t } from 'ttag'

function TariffCatalogListPage() {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()

  const { singleTariff, isSingleTariffLoading } = useSelectedTariff(tariffId)
  const {
    catalogs,
    isFetching: isCatalogsFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    selectedCatalogId,
    setSelectedCatalogId
  } = useCatalogsWithParams()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedCatalogId)

  const { searchText, handleChangePage, handleRequestSort, handleSearchTextChange, handleSearch } =
    useListFunctions({
      searchQueryParams,
      setSearchQueryParams
    })

  const breadcrumbs = [
    {
      title: t`Home`,
      link: PRODUCT_MANAGEMENT_ROOT_URL
    },
    {
      title: t`Tariffs`,
      link: baseUrl
    },
    {
      title: singleTariff ? singleTariff.description : ''
    },
    {
      title: t`Tariff sales channels`
    }
  ]

  const handleDrawerClose = () => {
    setSelectedCatalogId('')
    setIsDrawerOpen(false)
  }

  const handleRowClick = (id: string) => {
    setSelectedCatalogId(id)
    setIsDrawerOpen(true)
  }

  useEffect(() => {
    setSearchQueryParams(prevParams => ({ ...prevParams, tariff_id: tariffId }))
  }, [tariffId])

  const [deleteCatalog, { isLoading: isDeleteCatalogLoading }] = useDeleteSingleCatalogMutation()
  const handleCatalogDelete = () => {
    if (!selectedCatalogId) return

    // From tariff catalog list: Deleting the catalog with "clean" mode to ensure that the products in the catalog are not
    // deleted from the tariff as well. Catalog contains references to the tariff's products.
    deleteCatalog({ id: selectedCatalogId, mode: DeleteModes.CLEAN })
      .unwrap()
      .then(() => {
        handleDrawerClose()
        SnackbarUtils.success(t`Catalog successfully deleted.`)
      })
      .catch(({ data: errorResponse }: { data: ProductTariffError }) => {
        const { message } = errorResponse
        if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error deleting the catalog.`)
        }
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 2 }}>
        <Breadcrumbs items={breadcrumbs} />
        <PageTitle>{t`Tariff sales channels`}</PageTitle>
      </Box>
      <ListActions>
        <CustomButton
          onClick={() => navigate(`${baseUrl}/${tariffId}/sales-channels/create/`)}
          size="small"
          startIcon={<AddIcon />}
        >
          {t`Add sales channel`}
        </CustomButton>
        <ListSearch
          searchText={searchText}
          handleSearchTextChange={handleSearchTextChange}
          handleSearch={handleSearch}
          placeholder={t`Search by name or code`}
          sx={{ marginLeft: 'auto' }}
        />
      </ListActions>
      <Box pt={1}>
        <CatalogList
          rows={catalogs}
          isLoading={isCatalogsFetching || isSingleTariffLoading || isDeleteCatalogLoading}
          params={searchQueryParams}
          handleRequestSort={handleRequestSort}
          handleChangePage={handleChangePage}
          selectedCatalogId={selectedCatalogId}
          onRowClick={handleRowClick}
          count={totalResults}
          page={
            searchQueryParams.start_index
              ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
              : 0
          }
        />
      </Box>
      <WithLoader loading={isSingleTariffLoading}>
        {singleTariff && (
          <TariffCatalogDrawer
            open={isDrawerOpen}
            handleClose={handleDrawerClose}
            tariff={singleTariff}
            selectedCatalogId={selectedCatalogId}
            handleCatalogDelete={handleCatalogDelete}
          />
        )}
      </WithLoader>
    </Box>
  )
}

export default TariffCatalogListPage
